import { useEffect, useState } from 'react';
import { getItemsByJob, type QueueItem } from '../lib/syncQueue';
import { loadBlob } from '../lib/blobStore';

interface Props {
  jobId: string;
  onClose: () => void;
}

interface DeviceInfoSnapshot {
  context?: {
    category?: string;
    label?: string;
    description?: string;
    elevation?: string | null;
  };
  flagged?: boolean;
  note_text?: string;
  gps?: { lat: number; lng: number; accuracy_m: number } | null;
  ua?: string;
}

function deviceInfoOf(item: QueueItem): DeviceInfoSnapshot {
  return (item.metadata.device_info ?? {}) as DeviceInfoSnapshot;
}

function statusDotClass(status: QueueItem['status']): string {
  switch (status) {
    case 'confirmed':
      return 'bg-eva-teal';
    case 'uploading':
      return 'bg-bracket-blue';
    case 'pending':
      return 'bg-bracket-amber';
    case 'failed':
      return 'bg-red-500';
  }
}

function statusLabel(status: QueueItem['status']): string {
  switch (status) {
    case 'confirmed':
      return 'synced';
    case 'uploading':
      return 'uploading';
    case 'pending':
      return 'pending sync';
    case 'failed':
      return 'failed';
  }
}

export function SessionGallery({ jobId, onClose }: Props) {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<QueueItem | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const created: string[] = [];

    (async () => {
      try {
        const list = await getItemsByJob(jobId);
        if (cancelled) return;
        setItems(list);

        const entries: [string, string][] = [];
        for (const item of list) {
          try {
            const blob = await loadBlob(item.local_blob_key);
            if (cancelled) return;
            const url = URL.createObjectURL(blob);
            created.push(url);
            entries.push([item.id, url]);
          } catch {
            // missing blob — skip silently
          }
        }
        if (!cancelled) {
          setUrls(Object.fromEntries(entries));
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [jobId]);

  if (selected) {
    return (
      <SessionGalleryDetail
        item={selected}
        url={urls[selected.id]}
        onBack={() => setSelected(null)}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black text-white">
      <div className="flex items-center justify-between p-4">
        <div>
          <div className="text-lg font-semibold">
            {items.length} {items.length === 1 ? 'photo' : 'photos'}
          </div>
          <div className="text-xs opacity-60">this session</div>
        </div>
        <button
          onClick={onClose}
          className="rounded-full bg-white/15 px-4 py-2 text-sm font-semibold"
        >
          Back to camera
        </button>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-sm opacity-60">
          Loading photos…
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-8 text-center text-sm opacity-60">
          No photos yet. Take your first shot to see it here.
        </div>
      ) : (
        <div className="grid flex-1 grid-cols-3 gap-1 overflow-y-auto p-1">
          {items.map((item) => {
            const url = urls[item.id];
            const di = deviceInfoOf(item);
            return (
              <button
                key={item.id}
                onClick={() => setSelected(item)}
                className="relative aspect-square overflow-hidden bg-neutral-900"
              >
                {url ? (
                  <img
                    src={url}
                    alt={`Photo ${item.sequence_index}`}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-[10px] opacity-40">
                    no image
                  </div>
                )}
                <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1.5 text-[10px] font-semibold">
                  #{item.sequence_index}
                </span>
                <span
                  className={`absolute top-1 right-1 h-2.5 w-2.5 rounded-full ${statusDotClass(
                    item.status,
                  )}`}
                  title={statusLabel(item.status)}
                />
                {di.flagged && (
                  <span
                    className="absolute top-1 left-1 h-2.5 w-2.5 rounded-full bg-red-500"
                    title="flagged"
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SessionGalleryDetail({
  item,
  url,
  onBack,
}: {
  item: QueueItem;
  url: string | undefined;
  onBack: () => void;
}) {
  const di = deviceInfoOf(item);
  const ctx = di.context;
  const captured = new Date(item.metadata.captured_at);
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black text-white">
      <div className="flex items-center justify-between p-4">
        <button
          onClick={onBack}
          className="rounded-full bg-white/15 px-4 py-2 text-sm font-semibold"
        >
          ‹ Gallery
        </button>
        <div className="flex items-center gap-2 text-sm">
          <span
            className={`inline-block h-2.5 w-2.5 rounded-full ${statusDotClass(
              item.status,
            )}`}
          />
          <span>
            #{item.sequence_index} · {statusLabel(item.status)}
          </span>
        </div>
      </div>

      <div className="relative flex-1">
        {url ? (
          <img
            src={url}
            alt={`Photo ${item.sequence_index}`}
            className="absolute inset-0 h-full w-full object-contain"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-sm opacity-60">
            image unavailable
          </div>
        )}
      </div>

      <div className="space-y-2 bg-black/85 p-4 text-sm">
        {ctx && (
          <div>
            <div className="text-[10px] uppercase tracking-wide opacity-60">Context</div>
            <div>
              {ctx.category} · {ctx.label}
            </div>
            {ctx.description && (
              <div className="text-xs opacity-80">{ctx.description}</div>
            )}
          </div>
        )}
        <div className="flex items-center gap-3 text-xs opacity-80">
          <span>{captured.toLocaleString()}</span>
          {di.flagged && (
            <span className="rounded-full bg-red-500 px-2 py-0.5 font-semibold">
              flagged
            </span>
          )}
          {di.gps && (
            <span>
              {di.gps.lat.toFixed(5)}, {di.gps.lng.toFixed(5)}
            </span>
          )}
        </div>
        {di.note_text && (
          <div>
            <div className="text-[10px] uppercase tracking-wide opacity-60">Note</div>
            <div className="text-xs opacity-90">{di.note_text}</div>
          </div>
        )}
      </div>
    </div>
  );
}
